import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, FileText, CheckCircle2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { WhatLeavesReceipt } from "@/components/statement/WhatLeavesReceipt";
import { buildRows, type InsertRow } from "@/lib/statement/core/payload";

interface BankStatementImportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface ParsedTransaction {
  date: string;
  description: string;
  amount: number;
  type: "income" | "expense";
  category: string;
}

export const BankStatementImport = ({
  open,
  onOpenChange,
  onSuccess,
}: BankStatementImportProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<ParsedTransaction[]>([]);
  const [payload, setPayload] = useState<InsertRow[]>([]);
  const [step, setStep] = useState<"upload" | "preview" | "receipt" | "complete">("upload");
  const { toast } = useToast();
  const { formatCurrency } = useUserPreferences();

  const parseCSV = (text: string): ParsedTransaction[] => {
    const lines = text.split("\n").filter((line) => line.trim());
    const transactions: ParsedTransaction[] = [];

    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const values = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));

      if (values.length < 3) continue;

      // Assuming CSV format: Date, Description, Amount
      // You can adjust this based on your bank's CSV format
      const [dateStr, description, amountStr] = values;
      const amount = Math.abs(parseFloat(amountStr));
      const type = parseFloat(amountStr) >= 0 ? "income" : "expense";

      // Basic category assignment based on description
      let category = "Other";
      const desc = description.toLowerCase();
      if (desc.includes("salary") || desc.includes("payroll")) category = "Salary";
      else if (desc.includes("grocery") || desc.includes("supermarket")) category = "Food";
      else if (desc.includes("rent") || desc.includes("mortgage")) category = "Housing";
      else if (desc.includes("gas") || desc.includes("fuel")) category = "Transportation";
      else if (desc.includes("restaurant") || desc.includes("cafe")) category = "Food";
      else if (desc.includes("utility") || desc.includes("electric")) category = "Utilities";

      transactions.push({
        date: dateStr,
        description,
        amount,
        type,
        category,
      });
    }

    return transactions;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith(".csv")) {
        toast({
          title: "Invalid File",
          description: "Please upload a CSV file",
          variant: "destructive",
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  const handlePreview = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      setPreview(parsed);
      setStep("preview");
    } catch {
      toast({
        title: "Error",
        description: "Failed to parse CSV file",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Build the exact rows and show the "what leaves your device" receipt (D-008)
  // before anything is sent.
  const toReceipt = () => {
    setPayload(buildRows(preview.map((t) => ({ ...t })), "Imported"));
    setStep("receipt");
  };

  const handleImport = async () => {
    if (payload.length === 0) return;

    setLoading(true);
    setProgress(0);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const batchSize = 10;
      const totalBatches = Math.ceil(payload.length / batchSize);

      for (let i = 0; i < payload.length; i += batchSize) {
        const batch = payload.slice(i, i + batchSize);
        const { error } = await supabase.from("transactions").insert(
          // @ts-expect-error Known bug (backlog 3.13): rows carry no account_id, which the live table requires, so this insert is rejected. Remove this line with the fix.
          batch.map((t) => ({ ...t, user_id: user.id }))
        );

        if (error) throw error;

        const currentBatch = Math.floor(i / batchSize) + 1;
        setProgress((currentBatch / totalBatches) * 100);
      }

      setStep("complete");
      toast({
        title: "Success",
        description: `Imported ${payload.length} transactions`,
      });

      setTimeout(() => {
        onOpenChange(false);
        onSuccess();
        resetState();
      }, 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Import failed",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setFile(null);
    setPreview([]);
    setPayload([]);
    setStep("upload");
    setProgress(0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Bank Statement</DialogTitle>
          <DialogDescription>
            Upload your monthly bank statement CSV file to import transactions
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="csv-file">CSV File</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                />
                {file && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FileText className="h-4 w-4" />
                    {file.name}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg bg-muted p-4 space-y-2">
              <p className="text-sm font-medium">Expected CSV Format:</p>
              <code className="text-xs block">Date, Description, Amount</code>
              <p className="text-xs text-muted-foreground">
                Example: 2024-01-15, "Grocery Store", -45.50
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={handlePreview}
                disabled={!file || loading}
              >
                <Upload className="h-4 w-4" />
                Preview Import
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <p className="text-sm font-medium mb-2">
                Found {preview.length} transactions
              </p>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {preview.slice(0, 10).map((t, i) => (
                  <div
                    key={i}
                    className="text-xs p-2 rounded bg-muted flex justify-between items-center"
                  >
                    <div className="flex-1">
                      <span className="font-medium">{t.date}</span> - {t.description}
                    </div>
                    <div className="flex gap-4 items-center">
                      <span className="text-xs bg-background px-2 py-1 rounded">
                        {t.category}
                      </span>
                      <span
                        className={
                          t.type === "income"
                            ? "text-income-text font-medium"
                            : "text-destructive font-medium"
                        }
                      >
                        ${t.amount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
                {preview.length > 10 && (
                  <p className="text-xs text-muted-foreground text-center">
                    ... and {preview.length - 10} more
                  </p>
                )}
              </div>
            </div>

            {loading && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-xs text-center text-muted-foreground">
                  Importing... {Math.round(progress)}%
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setStep("upload")}
                disabled={loading}
              >
                Back
              </Button>
              <Button className="flex-1" onClick={toReceipt} disabled={loading}>
                Review what's sent
              </Button>
            </div>
          </div>
        )}

        {step === "receipt" && (
          <div className="space-y-4">
            <WhatLeavesReceipt
              rows={payload}
              onConfirm={handleImport}
              onBack={() => setStep("preview")}
              busy={loading}
              formatAmount={formatCurrency}
            />
            {loading && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-xs text-center text-muted-foreground">Importing… {Math.round(progress)}%</p>
              </div>
            )}
          </div>
        )}

        {step === "complete" && (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-income/10 dark:bg-income/20 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-income-text dark:text-income-text" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-lg">Import Complete!</h3>
              <p className="text-sm text-muted-foreground">
                {payload.length} transactions imported successfully
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
